/**
 * Buchhalterische (commercial) VAT math — gross ↔ net, in integer cents.
 *
 * Mirrors ``admin/app/vat.py``: the VAT line is commercially rounded (half
 * away from zero); the third value is the exact remainder, so
 * ``netCents + vatCents === grossCents`` in BOTH directions. The Python
 * module is authoritative for stored values; this mirror powers the
 * catalogue net display so it matches what the backend would compute.
 *
 * Net-entered amounts survive a round trip (net → gross → net recovers the
 * entered net), which is the property the net-based expense/procurement
 * entry will rely on.
 */

export interface VatBreakdown {
  grossCents: number;
  netCents: number;
  vatCents: number;
}

// Round to nearest integer, ties away from zero (commercial). JS
// Math.round already rounds positive halves up; abs/sign keeps it correct
// for any negative (refund) inputs too.
const roundHalfUp = (n: number): number =>
  Math.sign(n) * Math.round(Math.abs(n));

/** Decompose a GROSS (brutto) amount.
 *  ``vat = round(gross * p / (100 + p))``; ``net = gross - vat``. */
export function grossToNet(grossCents: number, percent: number): VatBreakdown {
  const vat = roundHalfUp((grossCents * percent) / (100 + percent));
  return { grossCents, netCents: grossCents - vat, vatCents: vat };
}

/** Compose a NET (netto) amount.
 *  ``vat = round(net * p / 100)``; ``gross = net + vat``. */
export function netToGross(netCents: number, percent: number): VatBreakdown {
  const vat = roundHalfUp((netCents * percent) / 100);
  return { grossCents: netCents + vat, netCents, vatCents: vat };
}
